import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Card, Stack } from '@mui/material';
import { isEqual } from 'lodash';
import { MultipleChoiceIndividualInput } from './MultipleChoiceIndividualInput';
import { FieldRenderProps } from '..';
import {
  ProjectStackField,
  ProjectStackDocument,
  MultipleChoiceOption,
  unsanitizeProjectFieldName,
} from '~/src/entities/project';
import { FieldHeaderWithEdit } from '../FieldHeader/FieldHeaderWithEdit';
import {
  useFieldMappingOverride,
  useSaveFieldMappingSnapshot,
} from '~/src/entities/fieldMapping';
import type { FieldMappingOverrideSubject } from '~/src/entities/fieldMapping/hooks/useFieldMappingOverride';
import { OptionLabel } from '~/src/pages/Populate/PopulatePageV2/PopulatePageContent/PopulateCardField/SingleMultipleFieldHelper/OptionLabel';
import {
  appendPicklistOptions,
  MultipleChoiceOptionWithPicklistMeta,
} from '~/src/pages/Populate/PopulatePageV2/PopulatePageContent/PopulateCardField/SingleMultipleFieldHelper/optionUtils';
import { useProjectContext } from '~/src/pages/Populate/ProjectProvider';

export type MultipleChoiceFieldProps = {
  field: FieldRenderProps;
  projectField: ProjectStackField;
  projectStackDocuments: ProjectStackDocument[];
  hasActiveMapping?: boolean;
  fieldMappingOverrideSubject?: FieldMappingOverrideSubject;
};

const tryToParseJson = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return '';
  }
};

export const MultipleChoiceField = React.memo(
  ({
    field,
    projectField,
    projectStackDocuments,
    hasActiveMapping,
    fieldMappingOverrideSubject,
  }: MultipleChoiceFieldProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const project = useProjectContext();

    const { onChange, onBlur, onFocus, countTouched } = field;

    const fieldMappingId =
      projectField.field_mapping_metadata?.field_mapping_id;
    const fieldMappingSourceName =
      projectField.field_mapping_metadata?.source_field;

    const { triggerOverride, triggerTextOverrideDebounced } =
      useFieldMappingOverride({
        hasActiveMapping,
        fieldMappingOverrideSubject,
        fieldMappingId,
        fieldMappingSourceName,
      });

    const { mutate: saveFieldMappingSnapshot } = useSaveFieldMappingSnapshot();

    const optionsFromDraft = projectField.multiple_choice_variable!.options;
    const sourceFieldPicklistOptions =
      projectField.field_mapping_metadata?.source_field_picklist_options;
    const sourceFieldName = projectField.field_mapping_metadata?.source_field;

    // Track which sourceFieldPicklistOptions we've already saved a snapshot for.
    // Using picklist options as the tracking key ensures we don't save stale data
    // when sourceFieldName changes before sourceFieldPicklistOptions updates (e.g. from optimistic update)
    const savedPicklistOptionsRef = useRef<
      Record<string, string> | null | undefined
    >(undefined);

    // Save/delete snapshot when field mapping changes
    useEffect(() => {
      const currentPicklistOptions = sourceFieldPicklistOptions;

      const shouldCallSnapshotApi = !isEqual(
        savedPicklistOptionsRef.current,
        currentPicklistOptions,
      );

      if (shouldCallSnapshotApi) {
        savedPicklistOptionsRef.current = currentPicklistOptions;
        saveFieldMappingSnapshot({
          projectId: project.id,
          data: {
            target_field_name: unsanitizeProjectFieldName(projectField.fid),
            source_field_name: hasActiveMapping
              ? sourceFieldName ?? null
              : null,
            source_picklist_options: currentPicklistOptions,
          },
        });
      }
    }, [
      hasActiveMapping,
      sourceFieldName,
      sourceFieldPicklistOptions,
      project.id,
      projectField.fid,
      saveFieldMappingSnapshot,
    ]);

    const isFieldValueFromPicklist = !tryToParseJson(field.value);

    const displayOptions = useMemo(() => {
      if (!hasActiveMapping && !sourceFieldPicklistOptions)
        return optionsFromDraft;
      return appendPicklistOptions(
        optionsFromDraft as MultipleChoiceOptionWithPicklistMeta[],
        sourceFieldPicklistOptions,
      );
    }, [hasActiveMapping, optionsFromDraft, sourceFieldPicklistOptions]);

    const memoizedStackSavedValue = useMemo(() => {
      const parsed = tryToParseJson(field.value) as string[];
      if (!Array.isArray(parsed)) {
        return Array.from({ length: displayOptions.length }, () => '');
      }
      return parsed;
    }, [field.value, displayOptions]);

    let stackSavedValue = memoizedStackSavedValue;
    const isStackDataCompatible =
      stackSavedValue.length === displayOptions.length;
    if (!isStackDataCompatible) {
      stackSavedValue = Array.from({ length: displayOptions.length }, () => '');
    }
    useEffect(() => {
      if (isStackDataCompatible) return;
      onChange(JSON.stringify(Array.from({ length: displayOptions.length })));
    }, [isStackDataCompatible, onChange, displayOptions.length]);

    const [textToMergeValues, setTextToMergeValues] = useState<string[]>(() => {
      return displayOptions.map(
        (opt, idx) => stackSavedValue[idx] || opt.value,
      );
    });

    useEffect(() => {
      const next = displayOptions.map(
        (opt, idx) => stackSavedValue[idx] || opt.value,
      );
      setTextToMergeValues((prev) => (isEqual(prev, next) ? prev : next));
    }, [stackSavedValue, displayOptions]);

    const isOptionChecked = (idx: number) => !!stackSavedValue[idx];

    const matchPickListOption = (picklistOption: MultipleChoiceOption) => {
      return picklistOption.value === field.value;
    };

    const handleChange = (idx: number, nextValue: string) => {
      triggerOverride();
      const nextValues = stackSavedValue.map((original, i) =>
        i === idx ? nextValue : original || '',
      );
      onChange(JSON.stringify(nextValues));
    };

    const canClose = textToMergeValues.every((v) => !!v);
    const isEdited = textToMergeValues.some(
      (v, idx) => v && v !== optionsFromDraft[idx]?.value,
    );

    return (
      <Card
        sx={(theme) => ({
          border: `1px solid ${
            isEditing ? theme.palette.border : 'transparent'
          }`,
          marginBottom: theme.spacing(3),
        })}
        elevation={isEditing ? 3 : 0}
      >
        <Box p={isEditing ? 2 : 0}>
          <Box>
            <FieldHeaderWithEdit
              projectField={projectField}
              projectStackDocuments={projectStackDocuments}
              isEdited={isEdited}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              canClose={canClose}
            />

            <Stack direction={'column'} spacing={1}>
              {displayOptions.map((option, idx) => {
                const isDraftOption = idx < optionsFromDraft.length;
                const textValue = textToMergeValues[idx] || '';
                const isChecked = isFieldValueFromPicklist
                  ? !isDraftOption && matchPickListOption(option)
                  : isOptionChecked(idx);
                const defaultValue = option.value;
                return (
                  <MultipleChoiceIndividualInput
                    key={`${option.value}-${idx}`}
                    label={
                      <OptionLabel
                        label={option.option}
                        showPicklistIcon={!!(option as any).isAppendedPicklist}
                      />
                    }
                    isChecked={isChecked}
                    showTextToMerge={isEditing}
                    inputRef={idx === 0 ? field.ref : undefined}
                    onChange={() => {
                      handleChange(idx, isChecked ? '' : textValue);
                      if (countTouched) {
                        countTouched(field.name);
                      }
                    }}
                    isEdited={displayOptions[idx]?.value !== textValue}
                    textToMergeValue={textValue}
                    onChangeTextToMergeValue={(value) => {
                      triggerTextOverrideDebounced();
                      setTextToMergeValues((values) =>
                        values.map((text, i) => (i === idx ? value : text)),
                      );
                    }}
                    onRevert={() => {
                      setTextToMergeValues((values) =>
                        values.map((text, i) =>
                          i === idx ? defaultValue : text,
                        ),
                      );
                      if (!isChecked) return;
                      handleChange(idx, defaultValue);
                    }}
                    onBlur={() => {
                      onBlur();
                      if (!isChecked) return;
                      handleChange(idx, textValue);
                    }}
                    onFocus={onFocus}
                  />
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.field.value === nextProps.field.value &&
      prevProps.projectField === nextProps.projectField &&
      prevProps.projectStackDocuments === nextProps.projectStackDocuments &&
      prevProps.hasActiveMapping === nextProps.hasActiveMapping
    );
  },
);
